import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import AppTheme from '../shared-theme/AppTheme';
import { GoogleIcon, FacebookIcon } from './CustomIcons';
import ColorModeSelect from '../shared-theme/ColorModeSelect';
import SitemarkIcon from '../marketing-page/components/SitemarkIcon';
import { CircularProgress, Grid } from '@mui/material';
import WorkIcon from "@mui/icons-material/Work";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import { CommunicationChannel } from '../../configuration/api/apiSchema';
import { LoadScript, Autocomplete } from '@react-google-maps/api';
import { useRef, useState } from 'react';



interface PasedProps {
    onSubmit: (email: string, password: string, firstname: string, lastname: string) => void;
    isLoading: boolean;
    isDuplicatedEmailError: boolean;
    selectedRole: 'handyman' | 'customer';
}

const SignUpForm = (props: PasedProps) => {
    const [emailError, setEmailError] = React.useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
    const [firstNameError, setFirstNameError] = React.useState(false);
    const [firstNameErrorMessage, setFirstNameErrorMessage] = React.useState('');
    const [lastNameError, setLastNameError] = React.useState(false);
    const [lastNameErrorMessage, setLastNameErrorMessage] = React.useState('');
    const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const [locationError, setLocationError] = React.useState(false);
    const [locationErrorMessage, setLocationErrorMessage] = React.useState('');

    React.useEffect(() => {
        if (props.isDuplicatedEmailError) {
            setEmailError(true);
            setEmailErrorMessage('Email already exists.');
        }
    }, [props.isDuplicatedEmailError])

    const validateInputs = () => {
        const email = document.getElementById('email') as HTMLInputElement;
        const password = document.getElementById('password') as HTMLInputElement;
        const firstname = document.getElementById('firstname') as HTMLInputElement;
        const lastname = document.getElementById('lastname') as HTMLInputElement;
        const locationInput = document.getElementById('location') as HTMLInputElement;

        let isValid = true;

        if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
            setEmailError(true);
            setEmailErrorMessage('Please enter a valid email address.');
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password.value || password.value.length < 6) {
            setPasswordError(true);
            setPasswordErrorMessage('Password must be at least 6 characters long.');
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (!firstname.value || firstname.value.length < 1) {
            setFirstNameError(true);
            setFirstNameErrorMessage('First name is required.');
            isValid = false;
        } else {
            setFirstNameError(false);
            setFirstNameErrorMessage('');
        }

        if (!lastname.value || lastname.value.length < 1) {
            setLastNameError(true);
            setLastNameErrorMessage('Last name is required.');
            isValid = false;
        } else {
            setLastNameError(false);
            setLastNameErrorMessage('');
        }

        if (props.selectedRole === 'handyman') {
            if (!locationInput.value || locationInput.value.length < 1) {
                setLocationError(true);
                setLocationErrorMessage('Location is required.');
                isValid = false;
            } else {
                setLocationError(false);
                setLocationErrorMessage('');
            }
        }

        return isValid;
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const isValid = validateInputs();

        if (!isValid) {
            return;
        }

        const data = new FormData(event.currentTarget);

        props.onSubmit(
            data.get('email') as string,
            data.get('password') as string,
            data.get('firstname') as string,
            data.get('lastname') as string
        );
    };

    return <>
        <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
        >
            Sign up
        </Typography>
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
            <FormControl>
                <FormLabel htmlFor="firstname">First name</FormLabel>
                <TextField
                    autoComplete="firstname"
                    name="firstname"
                    required
                    fullWidth
                    id="firstname"
                    placeholder="Jon"
                    error={firstNameError}
                    helperText={firstNameErrorMessage}
                    color={firstNameError ? 'error' : 'primary'}
                />
            </FormControl>
            <FormControl>
                <FormLabel htmlFor="lastname">Last name</FormLabel>
                <TextField
                    autoComplete="lastname"
                    name="lastname"
                    required
                    fullWidth
                    id="lastname"
                    placeholder="Snow"
                    error={lastNameError}
                    helperText={lastNameErrorMessage}
                    color={lastNameError ? 'error' : 'primary'}
                />
            </FormControl>
            <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <TextField
                    required
                    fullWidth
                    id="email"
                    placeholder="your@email.com"
                    name="email"
                    autoComplete="email"
                    variant="outlined"
                    error={emailError}
                    helperText={emailErrorMessage}
                    color={passwordError ? 'error' : 'primary'}
                />
            </FormControl>
            <FormControl>
                <FormLabel htmlFor="location">Location</FormLabel>
                <Autocomplete
                    onLoad={(autocomplete) => {
                        autocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={() => {
                        const place = autocompleteRef.current?.getPlace();
                        if (place?.formatted_address) {
                            setSelectedAddress(place.formatted_address);
                        }
                    }}
                >
                    <TextField
                        fullWidth
                        name="location"
                        id="location"
                        placeholder="Enter your service area address"
                        variant="outlined"
                        error={locationError}
                        helperText={locationErrorMessage}
                        color={locationError ? 'error' : 'primary'}
                    />
                </Autocomplete>
            </FormControl>

            <FormControl>
                <FormLabel htmlFor="password">Password</FormLabel>
                <TextField
                    required
                    fullWidth
                    name="password"
                    placeholder="••••••"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    variant="outlined"
                    error={passwordError}
                    helperText={passwordErrorMessage}
                    color={passwordError ? 'error' : 'primary'}
                />
            </FormControl>
            <FormControlLabel
                control={<Checkbox value="allowExtraEmails" color="primary" />}
                label="I want to receive updates via email."
            />
            <Button
                type="submit"
                fullWidth
                variant="contained"
                onClick={validateInputs}
                disabled={props.isLoading}
            >
                {props.isLoading ? (
                    <CircularProgress
                        size={24}
                        style={{
                            color: "white",
                            position: "absolute",
                        }}
                    />
                ) : (
                    "Sign up"
                )}
            </Button>
        </Box>
        <Divider>
            <Typography sx={{ color: 'text.secondary' }}>or</Typography>
        </Divider>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
                fullWidth
                variant="outlined"
                onClick={() => alert('Sign up with Google')}
                startIcon={<GoogleIcon />}
                disabled
            >
                Sign up with Google
            </Button>
            <Button
                fullWidth
                variant="outlined"
                onClick={() => alert('Sign up with Facebook')}
                startIcon={<FacebookIcon />}
                disabled
            >
                Sign up with Facebook
            </Button>
            <Typography sx={{ textAlign: 'center' }}>
                Already have an account?{' '}
                <Link
                    href="sign-in"
                    variant="body2"
                    sx={{ alignSelf: 'center' }}
                >
                    Sign in
                </Link>
            </Typography>
        </Box>
    </>
}

export default SignUpForm